/**
 * Shell-level helpers shared by the rail, the folders sheet, the folder context menu, the top
 * bar and the list: folder sections, the prompt/confirm flows of spec §8, and the row actions
 * every surface triggers the same way (delete with the Trash confirmation, star toggle, …).
 *
 * Nothing here is a store gap; the store already exposes the verbs, these functions only wire
 * the dialogs and the folder-tree bookkeeping around them.
 */
import { t } from '$lib/i18n';
import { badgeText } from '$lib/i18n';
import {
  customFolderRows,
  delimiterOf,
  inboxPath,
  isCustomFolder,
  leafName,
  limitTopLevel,
  pathContext,
  rewriteCollapsed,
  splitFolders,
  type FolderNode,
  type PathContext,
} from '$lib/mail/folders';
import { confirm, prompt, store } from '$lib/store';
import type { FolderRec, ThreadRow } from '$lib/types';
import { folderUi } from './folderUi.svelte';

export interface FolderSections {
  special: FolderRec[];
  custom: FolderNode[];
  hidden: number;
  ctx: PathContext;
  delimiter: string;
  /** Whether the "show more / show less" button should be offered. */
  showMore: boolean;
}

/** Rail / sheet sections (spec §8): specials by order, customs as a limited, collapsible tree. */
export function folderSections(folders: readonly FolderRec[], collapsed: ReadonlySet<string>, showAll: boolean, currentFolder: string): FolderSections {
  const ctx = pathContext(folders);
  const delimiter = delimiterOf(folders);
  const { special, custom } = splitFolders(folders, ctx);
  const customOnly = custom.filter((f) => isCustomFolder(f, ctx));
  const rows = customFolderRows(customOnly, collapsed, delimiter);
  const limited = limitTopLevel(rows, { showAll, currentFolder, delimiter });
  const tops = rows.filter((n) => n.depth === 0).length;
  return { special, custom: limited.rows, hidden: limited.hidden, ctx, delimiter, showMore: limited.hidden > 0 || (showAll && tops > 5) };
}

export const badge = badgeText;

export function isCustom(f: FolderRec, ctx: PathContext): boolean {
  return isCustomFolder(f, ctx);
}

// ---- folder flows (spec §8) -----------------------------------------------------------------

export async function newFolderFlow(): Promise<void> {
  const name = await prompt({ title: t('newFolder'), placeholder: t('folderNamePrompt'), ok: t('create') });
  if (name) await store.newFolder(name);
}

export async function newSubfolderFlow(path: string, name: string): Promise<void> {
  const leaf = await prompt({ title: t('newSubfolderPrompt', { f: name }), placeholder: t('folderNamePrompt'), ok: t('create') });
  if (!leaf) return;
  folderUi.expand(path);
  await store.newFolder(leaf, path);
}

export async function renameFolderFlow(path: string, name: string): Promise<void> {
  const delimiter = delimiterOf(store.folders);
  const leaf = leafName(path, name, delimiter);
  const next = await prompt({ title: t('renameF'), value: leaf, placeholder: t('renamePrompt'), ok: t('save') });
  if (!next || next === leaf) return;
  const i = path.lastIndexOf(delimiter);
  const newPath = (i > 0 ? path.slice(0, i) + delimiter : '') + next;
  folderUi.replaceCollapsed(rewriteCollapsed(folderUi.collapsed, path, newPath, delimiter));
  await store.renameFolder(path, next);
}

export async function deleteFolderFlow(path: string, name: string): Promise<void> {
  const ok = await confirm({ title: t('delFolder'), body: t('confirmDelFolder', { f: name }), ok: t('del'), danger: true });
  if (ok) await store.deleteFolder(path);
}

export async function emptyFolderFlow(): Promise<void> {
  if (!store.canEmpty()) return;
  const ok = await confirm({ title: t('emptyNow'), body: t('confirmEmpty'), ok: t('del'), danger: true });
  if (ok) await store.emptyFolder();
}

export async function createArchiveFlow(): Promise<void> {
  await store.newFolder('Archive');
}

// ---- row / selection actions ------------------------------------------------------------------

/** Delete, asking `confirmPurge` first when the folder is the Trash (spec §17 `#`). */
export async function deleteUids(uids: readonly number[], folder = store.currentFolder): Promise<void> {
  if (!uids.length) return;
  if (store.isPurge(folder)) {
    const ok = await confirm({ title: t('confirmTitle'), body: t('confirmPurge'), ok: t('del'), danger: true });
    if (!ok) return;
  }
  await store.remove([...uids], folder);
}

/** Star when any of the uids is unstarred, otherwise unstar. */
export async function toggleStar(uids: readonly number[], folder = store.currentFolder): Promise<void> {
  if (!uids.length) return;
  const rows = store.msgs.filter((m) => m.folder === folder && uids.includes(m.uid));
  const allFlagged = rows.length > 0 && rows.every((m) => m.flags.includes('\\Flagged'));
  await store.act(allFlagged ? 'unflag' : 'flag', folder, uids);
}

export async function markSeen(uids: readonly number[], seen: boolean, folder = store.currentFolder): Promise<void> {
  if (!uids.length) return;
  await store.act(seen ? 'seen' : 'unseen', folder, uids, undefined, { undoable: true });
}

/** Prompt for a label (default = first known label) and apply it. */
export async function addLabelFlow(uids: readonly number[]): Promise<void> {
  if (!uids.length) return;
  const name = await prompt({ title: t('addLabel'), value: store.knownLabels[0] ?? '', placeholder: t('labelName'), ok: t('add') });
  if (name) await store.setLabel([...uids], name.trim(), true);
}

/** In Junk: move back to the inbox (`notJunk`); elsewhere: move to Junk. */
export async function toggleJunk(uids: readonly number[]): Promise<void> {
  const junk = store.junkPath();
  if (!junk || !uids.length) return;
  const dest = store.currentFolder === junk ? inboxPath(store.folders) : junk;
  await store.moveTo(dest, [...uids]);
}

export function isInJunk(): boolean {
  const junk = store.junkPath();
  return !!junk && store.currentFolder === junk;
}

/** Whether rows in `folder` can be archived (an archive folder exists and this is not it). */
export function canArchiveIn(folder = store.currentFolder): boolean {
  const a = store.archivePath;
  return !!a && a !== folder;
}

/** Uids of a row (thread rows carry every member). */
export function rowUids(row: ThreadRow): number[] {
  return row.uids?.length ? row.uids : [row.uid];
}

/** Whether the current selection belongs to the folder on screen. */
export function selectionActive(): boolean {
  return store.selection.size > 0 && (store.selectionFolder ?? store.currentFolder) === store.currentFolder;
}
